import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Ornament } from "../components/Ornament";
import { LanguageSelector } from "../components/LanguageSelector";
import LegalFooter from "../components/LegalFooter";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { EDITIONS, SUBSCRIPTION_TIERS, B2B_TIERS } from "../data/content";

const QUOTES = [
  { q: "In family life, love is the oil that eases friction.", a: "Friedrich Nietzsche" },
  { q: "Family is not an important thing. It's everything.", a: "Michael J. Fox" },
  { q: "Families are the compass that guides us.", a: "Brad Henry" },
];

/** Fade-up list-item that reveals on scroll. Uses IntersectionObserver;
 *  the CSS class `.fade-up` already handles prefers-reduced-motion. */
function FadeUpLi({ children, className, testid, delayMs = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    // If user prefers reduced motion, reveal immediately (no observer needed)
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <li
      ref={ref}
      className={`fade-up ${visible ? "is-visible" : ""} ${className || ""}`}
      style={{ transitionDelay: `${delayMs}ms` }}
      data-testid={testid}
    >
      {children}
    </li>
  );
}

const STEPS = [
  { num: "I",   title: "Choose a chapter",  body: "Seven beautifully written chapters guide you — Childhood, School Days, Family & Home, Love, Work, History, and Wisdom." },
  { num: "II",  title: "Speak or write",    body: "One question at a time. Tap the microphone, speak as you would to a grandchild — we'll transcribe it, and keep the original recording forever." },
  { num: "III", title: "We gently draw it out", body: "A warm, considered follow-up question appears — drawing out the colour, the people, the feeling." },
  { num: "IV",  title: "Receive your book", body: "Pages are typeset into a true heirloom: a premium hardback with superior paper stock, your family's photographs woven through." },
];

const DIFFERENTIATORS = [
  { title: "Voice-first by design", body: "Built for the people whose stories matter most — many of whom find typing tiring. Tap, speak, done." },
  { title: "Voice and text together", body: "We keep your original voice recording alongside the transcript, so future readers can hear it in your own words." },
  { title: "An AI that listens", body: "Our follow-up questions reference earlier stories — so a granddaughter mentioned in Chapter I might be gently remembered in Chapter IV." },
  { title: "Multiple voices, one family", body: "Grandmother, father, aunt and grandchild each have their own space within a shared family archive." },
  { title: "A living book", body: "Add new stories and new photographs whenever you're ready — your Living Book grows with your family." },
  { title: "Made to be handed on", body: "Personalised printed keepsakes will be offered separately when fulfilment is ready." },
];

export default function LandingPage() {
  const { t } = useTranslation();
  const { setUser, user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Story Week signup state
  const [sw, setSw] = useState({ name: "", email: "", consent: false });
  const [swError, setSwError] = useState("");
  const [swBusy, setSwBusy] = useState(false);
  // Book Club founding-list state (temporarily hidden from public homepage; kept for later)
  const [club, setClub] = useState({ name: "", email: "", city: "", role: "either", consent: false });
  const [clubSubmitted, setClubSubmitted] = useState(false);
  const [clubError, setClubError] = useState("");
  const [clubBusy, setClubBusy] = useState(false);
  // B2B form state (temporarily hidden from public homepage; kept for later)
  const [b2bOpen, setB2bOpen] = useState(false);
  const [b2b, setB2b] = useState({ org_name: "", org_type: "care_home", size: "", contact_name: "", contact_email: "", message: "" });
  const [b2bSent, setB2bSent] = useState(false);
  const [b2bError, setB2bError] = useState("");
  const navigate = useNavigate();

  // Feature flags — temporarily hide unfinished sections from the public homepage
  // WITHOUT deleting their code. Flip to true to restore.
  const SHOW_BOOK_CLUB = false;
  const SHOW_B2B = false;
  const SHOW_EDITIONS = false;
  const SHOW_WAITLIST = false;
  const SHOW_LANGUAGE_SELECTOR = false;
  // Trims the pre-launch homepage to only the eight sections the audit specifies.
  // Hero, How it works, Story Week, The Book, Membership, Founder, Final CTA, Footer.
  const SHOW_HOMEPAGE_EXTRAS = false;

  // The Living Questions — Amazon URL (configurable). Uses env if set, otherwise fallback.
  const AMAZON_URL = (process.env.REACT_APP_LIVING_QUESTIONS_AMAZON_URL || "").trim();

  useEffect(() => {
    api.get("/stats/founding").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Scroll to hash target on initial load / refresh. The homepage is a single
  // long React component so #how, #story-week etc. do not resolve until after
  // the sections have mounted. This runs after paint and again after images
  // finish loading (which can shift layout).
  useEffect(() => {
    const scrollToHash = () => {
      const id = (window.location.hash || "").replace(/^#/, "");
      if (!id) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    // wait a paint tick so sections are in the DOM
    const t1 = setTimeout(scrollToHash, 60);
    // re-run after the browser is done with the initial load (images / fonts settled)
    const onLoad = () => setTimeout(scrollToHash, 100);
    window.addEventListener("load", onLoad, { once: true });
    // Support hash-only clicks (in-page navigation) that some browsers ignore in SPAs.
    const onHashChange = () => scrollToHash();
    window.addEventListener("hashchange", onHashChange);
    return () => {
      clearTimeout(t1);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const foundingOpen = stats?.available !== false;
  const remaining = stats?.remaining;

  // ---- Returning-user smart Story Week CTA -----------------------------
  // For signed-in users, we look up their Story Week progress so every
  // primary CTA reads correctly:  "ANSWER QUESTION ONE" (no answers),
  // "Continue Story Week" (in progress), or "Open my Living Book" (all
  // seven done). Signed-out users always see "ANSWER MY FIRST QUESTION"
  // with a secondary "Already started?" sign-in link.
  const [swSession, setSwSession] = useState(null);
  useEffect(() => {
    if (!user) { setSwSession(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/story_week/mine");
        if (alive) setSwSession(r.data);
      } catch (e) { /* no session yet — treated as "no answers" below */ }
    })();
    return () => { alive = false; };
  }, [user]);

  const swCta = React.useMemo(() => {
    // Signed-out: canonical Story Week starter
    if (!user) {
      return {
        label: "Start Story Week — free",
        go: () => navigate("/login?flow=story_week&next=%2Fstory-week%2Fsetup"),
        showSignInLink: false,
      };
    }
    const days = swSession?.days || {};
    const answered = Object.keys(days).filter((k) => (days[k]?.text || "").trim()).length;
    const skipped = new Set(swSession?.skipped_days || []);
    const touched = answered + skipped.size;
    if (touched === 0) {
      return {
        label: "Start Story Week — free",
        go: () => navigate("/story-week/day/1"),
        showSignInLink: false,
      };
    }
    if (touched >= 7) {
      return {
        label: "Open my Living Book",
        go: () => navigate("/my-living-book"),
        showSignInLink: false,
      };
    }
    // In progress — find next available unanswered non-skipped day (capped
    // at the user's available_day). If none exists (they answered every
    // currently-open day but future ones are still locked), route to
    // /my-living-book so they can see progress + the exact unlock time.
    const avail = swSession?.available_day || 1;
    const cap = Math.min(7, avail);
    let nextDay = null;
    for (let i = 1; i <= cap; i++) {
      if (!days[String(i)] && !skipped.has(i)) { nextDay = i; break; }
    }
    if (nextDay == null) {
      return {
        label: "Continue Story Week",
        go: () => navigate("/my-living-book"),
        showSignInLink: false,
      };
    }
    return {
      label: "Continue Story Week",
      go: () => navigate(`/story-week/day/${nextDay}`),
      showSignInLink: false,
    };
  }, [user, swSession, navigate]);

  /** Secondary "Already started? Sign in to my Living Book" — rendered
   *  under every primary CTA when signed out. `tone` picks the right
   *  colour for dark hero vs. cream sections. */
  const AlreadyStartedLink = ({ tone = "cream", testid }) => {
    if (!swCta.showSignInLink) return null;
    const color = tone === "cream" ? "#F2E3B8" : "#B8973A";
    const hover = tone === "cream" ? "#FFFFFF" : "#111D33";
    return (
      <p className="mt-4">
        <Link
          to="/login?next=%2Fmy-living-book"
          className="inline-block underline underline-offset-4"
          style={{ color, fontFamily: "Georgia, serif", fontSize: "15px", minHeight: 44, lineHeight: "24px", padding: "10px 0" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = color; }}
          data-testid={testid || "cta-already-started"}
        >
          Already started? Sign in to my Living Book
        </Link>
      </p>
    );
  };

  const joinWaitlist = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    try {
      const res = await api.post("/auth/quickstart", { email, source: "landing-bottom" });
      if (res.data?.session_token) localStorage.setItem("session_token", res.data.session_token);
      setUser({ user_id: res.data.user_id, email: res.data.email, name: res.data.name });
      navigate("/story-week");
    } catch (e) {
      setError("Something went wrong. Try again?");
    }
  };

  const beginStoryWeek = async (e) => {
    e?.preventDefault?.();
    setSwError("");
    if (!sw.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sw.email)) {
      setSwError("Please enter a valid email.");
      return;
    }
    if (!sw.consent) {
      setSwError("Please tick the consent box to continue.");
      return;
    }
    setSwBusy(true);
    try {
      const res = await api.post("/auth/quickstart", {
        email: sw.email,
        name: sw.name || null,
        source: "story-week-landing",
      });
      if (res.data?.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      setUser({ user_id: res.data.user_id, email: res.data.email, name: res.data.name });
      try { localStorage.setItem("tlb_landing_name", sw.name || res.data.name || ""); } catch (e) { /* ignore */ }
      navigate("/story-week");
    } catch (e) {
      setSwError("Something went wrong signing you up. Please try again.");
      setSwBusy(false);
    }
  };

  const joinBookClub = async (e) => {
    e?.preventDefault?.();
    setClubError("");
    if (!club.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(club.email)) {
      setClubError("Please enter a valid email.");
      return;
    }
    if (!club.city.trim()) {
      setClubError("Please add your town or city.");
      return;
    }
    if (!club.consent) {
      setClubError("Please tick the consent box to continue.");
      return;
    }
    setClubBusy(true);
    try {
      await api.post("/waitlist", {
        email: club.email,
        name: club.name || null,
        city: club.city.trim(),
        role: club.role,
        source: "book-club",
      });
      setClubSubmitted(true);
    } catch (e) {
      setClubError("Something went wrong. Try again?");
    } finally {
      setClubBusy(false);
    }
  };

  const submitB2B = async (e) => {
    e.preventDefault();
    setB2bError("");
    if (!b2b.org_name.trim() || !b2b.contact_email.trim()) {
      setB2bError("Please add at least your organisation and contact email.");
      return;
    }
    try {
      await api.post("/b2b/enquiry", { ...b2b, size: b2b.size || null, contact_name: b2b.contact_name || null, message: b2b.message || null });
      setB2bSent(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setB2bError(Array.isArray(d) ? (d[0]?.msg || "Please check the details.") : (typeof d === "string" ? d : "Couldn't send. Please try again."));
    }
  };

  return (
    <div className="bg-cream text-navy-deep" style={{ overflowX: "hidden" }} data-testid="landing-page">
      {/* NAV */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-10 py-6" aria-label="Primary">
          <div className="flex items-center gap-3">
            <span className="text-[#E8D5A3] text-2xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
            <span className="text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.4rem", letterSpacing: "0.08em" }}>The Living Book</span>
          </div>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {[
              { l: "Story Week", h: "#story-week" },
              { l: "How it works", h: "#how" },
              { l: "The Living Questions", h: "#the-book" },
              { l: "Founding Beta", h: "#founding-beta" },
            ].map((it, i) => (
              <a key={i} href={it.h} className="label-ui text-[11px] text-[#E8D5A3] hover:text-white transition-colors duration-500" data-testid={`nav-link-${i}`}>
                {it.l}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            {SHOW_LANGUAGE_SELECTOR && <LanguageSelector tone="dark" />}
            {user ? (
              <button
                onClick={() => navigate("/my-living-book")}
                className="hidden md:inline-flex btn-outline-cream"
                data-testid="nav-mylivingbook-btn"
                style={{ minHeight: 44 }}
              >
                My Living Book
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="hidden md:inline-flex btn-outline-cream"
                data-testid="nav-signin-btn"
                style={{ minHeight: 44 }}
              >
                Sign in
              </button>
            )}
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileNavOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center text-[#FAF7F2] hover:text-white"
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav"
              style={{ width: 44, height: 44, border: "1px solid #B8973A", borderRadius: 4 }}
              data-testid="mobile-nav-toggle"
            >
              {mobileNavOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
              )}
            </button>
          </div>
        </nav>
        {/* Mobile menu panel */}
        {mobileNavOpen && (
          <div
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="md:hidden absolute left-0 right-0 top-full mx-4 mt-2 shadow-2xl"
            style={{ background: "#111D33", border: "1px solid #B8973A", borderRadius: 4 }}
            data-testid="mobile-nav-panel"
          >
            <ul className="flex flex-col py-2">
              {[
                { l: "Story Week", h: "#story-week" },
                { l: "How it works", h: "#how" },
                { l: "The Living Questions", h: "#the-book" },
                { l: "Founding Beta", h: "#founding-beta" },
              ].map((it, i) => (
                <li key={i}>
                  <a
                    href={it.h}
                    onClick={() => setMobileNavOpen(false)}
                    className="block px-6 py-4 label-ui text-[12px] text-[#E8D5A3] hover:text-white hover:bg-[#1B2A4A] transition-colors"
                    style={{ minHeight: 44 }}
                    data-testid={`mobile-nav-link-${i}`}
                  >
                    {it.l}
                  </a>
                </li>
              ))}
              <li className="border-t border-[#1B2A4A] mt-1">
                {user ? (
                  <button
                    type="button"
                    onClick={() => { setMobileNavOpen(false); navigate("/my-living-book"); }}
                    className="w-full text-left px-6 py-4 label-ui text-[12px] text-[#B8973A] hover:text-white hover:bg-[#1B2A4A] transition-colors"
                    style={{ minHeight: 44 }}
                    data-testid="mobile-nav-mylivingbook"
                  >
                    My Living Book →
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setMobileNavOpen(false); navigate("/login"); }}
                    className="w-full text-left px-6 py-4 label-ui text-[12px] text-[#B8973A] hover:text-white hover:bg-[#1B2A4A] transition-colors"
                    style={{ minHeight: 44 }}
                    data-testid="mobile-nav-signin"
                  >
                    Sign in →
                  </button>
                )}
              </li>
            </ul>
          </div>
        )}
      </header>

      {/* HERO */}
      <section
        className="relative paper-grain"
        style={{
          background: "#FAF7F2",
          minHeight: "calc(92vh - 76px)",
          padding: "88px 24px 48px",
          overflow: "hidden",
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: "radial-gradient(900px 480px at 12% 15%, rgba(184,151,58,0.06), transparent 60%), radial-gradient(700px 420px at 92% 88%, rgba(17,29,51,0.04), transparent 60%)" }} />

        <style>{`
          .hero-grid {
            position: relative;
            display: grid;
            grid-template-columns: 1fr;
            row-gap: 20px;
            max-width: 1200px;
            margin: 0 auto;
          }
          .hero-grid > * { margin: 0; min-width: 0; }

          .hero-grid .hg-kicker {
            font-family: 'DM Sans', sans-serif;
            font-size: 12px;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            font-weight: 500;
            color: #B8973A;
          }
          .hero-grid .hg-headline {
            font-family: 'Newsreader', serif;
            font-weight: 500;
            color: #111D33;
            font-size: clamp(34px, 4.2vw, 56px);
            line-height: 1.08;
            letter-spacing: -0.01em;
            max-width: 20ch;
          }
          .hero-grid p.hg-body,
          .hero-grid p.hg-tail,
          .hero-grid p.hg-trust {
            font-family: 'DM Sans', sans-serif;
            line-height: 1.6;
            max-width: 36em;
          }
          .hero-grid .hg-body  { font-size: 18px; color: #3A4A66; }
          .hero-grid .hg-trust { font-size: 14px; color: #6B6355; letter-spacing: 0.01em; }
          .hero-grid .hg-signin {
            font-family: 'DM Sans', sans-serif;
            font-size: 15px;
            color: #111D33;
            text-decoration: underline;
            text-underline-offset: 4px;
            text-decoration-color: rgba(17,29,51,0.3);
          }
          .hero-grid .hg-signin:hover { text-decoration-color: #111D33; }
          .hero-grid .hg-buttons {
            display: flex; flex-wrap: wrap; align-items: center; gap: 20px;
            margin-top: 6px;
          }

          .hero-photo-wrap {
            position: relative;
            width: 100%;
            max-width: 460px;
            margin: 0 auto;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 32px 64px -40px rgba(17,29,51,0.35);
          }
          .hero-photo-img {
            width: 100%;
            height: auto;
            aspect-ratio: 4 / 5;
            object-fit: cover;
            display: block;
            border-radius: 16px;
            filter: saturate(0.95) contrast(0.98);
          }

          .hero-grid .hg-kicker    { order: 1; }
          .hero-grid .hg-headline  { order: 2; }
          .hero-grid .hg-body      { order: 3; }
          .hero-grid .hg-buttons   { order: 4; }
          .hero-grid .hg-signin    { order: 5; }
          .hero-grid .hg-trust     { order: 6; }
          .hero-grid .hg-photo     { order: 7; }
          @media (max-width: 899px) {
            .hero-photo-wrap { max-width: 320px; }
          }

          @media (min-width: 900px) {
            .hero-grid {
              grid-template-columns: 1fr minmax(0, 0.85fr);
              column-gap: 72px;
              row-gap: 20px;
              align-items: center;
              padding: 0 48px;
            }
            .hero-grid .hg-kicker,
            .hero-grid .hg-headline,
            .hero-grid .hg-body,
            .hero-grid .hg-buttons,
            .hero-grid .hg-signin,
            .hero-grid .hg-trust    { grid-column: 1; order: initial; }
            .hero-grid .hg-photo    {
              grid-column: 2;
              grid-row: 1 / span 7;
              justify-self: center;
              align-self: center;
              order: initial;
            }
          }
        `}</style>

        <div className="hero-grid">
          <p className="hg-kicker" data-testid="hero-eyebrow">A PRIVATE FAMILY STORY APP</p>
          <h1 className="hg-headline" data-testid="hero-headline">
            There are stories in your family you&rsquo;ve never heard.
          </h1>
          <p className="hg-body" data-testid="hero-body">
            The Living Book gives you one thoughtful question at a time. Sit down with someone you love, record the answer and begin a private book of their stories&mdash;in their own words and voice.
          </p>
          <div className="hg-buttons">
            <button
              onClick={swCta.go}
              className="btn-gold"
              data-testid="hero-storyweek-btn"
            >
              Start Story Week &mdash; free
            </button>
          </div>
          <Link
            to="/login?next=%2Fmy-living-book"
            className="hg-signin"
            data-testid="hero-signin-link"
          >
            Already started? Sign in to my Living Book
          </Link>
          <p className="hg-trust" data-testid="hero-trust">
            Seven questions. No card needed. Private to your family.
          </p>
          <div className="hg-photo" data-testid="hero-portrait-wrap">
            <div className="hero-photo-wrap" style={{
              aspectRatio: "4 / 5",
              background: "linear-gradient(135deg, #FFFDF8 0%, #F5EDD9 55%, #ECE0C0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "32px",
              border: "1px solid #F0EAE0",
            }}>
              <div style={{ textAlign: "center", color: "#8C7A4A" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 999, border: "1px solid #B8973A",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "#B8973A", fontSize: 24, marginBottom: 18, fontFamily: "'Newsreader', serif",
                }}>+</div>
                <p style={{ fontFamily: "'Newsreader', serif", fontStyle: "italic", fontSize: "1.1rem", lineHeight: 1.4, maxWidth: "26ch", margin: "0 auto 10px" }}>
                  Hero photograph placeholder
                </p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#9A8D6A", margin: 0 }}>
                  Natural, candid moment — adult child and parent at a kitchen table
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO STATEMENT — the brand line, no longer H1 */}
      <section className="paper-grain py-10 sm:py-12 px-6 sm:px-10 bg-[#FAF7F2]" aria-label="Our belief">
        <div className="max-w-4xl mx-auto flex items-baseline gap-6">
          <span className="hidden sm:inline chapter-roman shrink-0" style={{ fontSize: "0.85rem" }}>&mdash; a note</span>
          <p className="text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(1.5rem, 3.4vw, 2.2rem)", lineHeight: 1.35, fontStyle: "italic" }} data-testid="manifesto-line">
            A whole world lives in one person&rsquo;s memory.
          </p>
        </div>
      </section>

      <section id="story-week" className="paper-grain pt-8 pb-10 sm:pt-10 sm:pb-12 px-6 sm:px-10 bg-[#FAF7F2]" data-testid="section-story-week">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-start">
          {/* Left column — folio + eyebrow + phone mockup */}
          <div className="md:col-span-4">
            <p className="chapter-roman" style={{ fontSize: "1.4rem", fontWeight: 600, letterSpacing: "0.18em" }}>DAY I &mdash; VII</p>
            <p className="label-ui text-[16px] text-[#B8973A] mt-4 tracking-[0.35em]" style={{ fontWeight: 700 }}>STORY WEEK</p>
            <p className="mt-6 text-[#5C5548] italic hidden md:block" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", lineHeight: 1.55, maxWidth: "16rem" }}>
              A seven-day journal. The first pages of your Living Book.
            </p>

            {/* Phone mockup — pure decorative, not interactive */}
            <div className="mt-10 mx-auto md:mx-0" role="img" aria-label="Preview of Day One: Is there a song or piece of music that takes you back to your childhood? What do you remember when you hear it?" data-testid="phone-mockup" style={{ maxWidth: 240 }}>
              <div
                aria-hidden="true"
                style={{
                  position: "relative",
                  aspectRatio: "9 / 11",
                  background: "#111D33",
                  borderRadius: 28,
                  padding: 10,
                  boxShadow: "0 24px 50px -30px rgba(17,29,51,0.45), 0 0 0 1px rgba(184,151,58,0.35)",
                }}
              >
                {/* screen */}
                <div style={{
                  position: "absolute", inset: 10,
                  background: "#FAF7F2",
                  borderRadius: 20,
                  overflow: "hidden",
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: "20px 16px 18px",
                }}>
                  {/* notch */}
                  <div aria-hidden="true" style={{
                    position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
                    width: 62, height: 5, background: "#111D33", borderRadius: 3,
                  }} />
                  <p style={{
                    fontFamily: "Montserrat, sans-serif", color: "#B8973A",
                    fontSize: 8.5, letterSpacing: "0.28em", marginTop: 4, marginBottom: 10,
                  }}>DAY ONE</p>
                  <p style={{
                    fontFamily: "Cormorant Garamond, serif", color: "#111D33",
                    fontStyle: "italic", fontSize: 13.5, lineHeight: 1.32, textAlign: "center",
                    marginBottom: 14, padding: "0 4px",
                  }}>
                    &ldquo;Is there a song or piece of music that takes you back to your childhood? What do you remember when you hear it?&rdquo;
                  </p>
                  {/* record button — sits directly beneath the question */}
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    background: "#B8973A",
                    boxShadow: "0 0 0 4px rgba(184,151,58,0.18), 0 6px 14px -4px rgba(184,151,58,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: 10,
                  }}>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#FAF7F2" }} />
                  </div>
                  <p style={{
                    fontFamily: "Georgia, serif", color: "#8C8278",
                    fontSize: 11, fontStyle: "italic",
                  }}>
                    <span style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>or write instead</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column — content */}
          <div className="md:col-span-8">
            <h2 className="text-4xl sm:text-5xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.1 }} data-testid="story-week-headline">
              Seven days. Seven questions. <span style={{ fontStyle: "italic" }}>Start here.</span>
            </h2>
            <div className="mt-8 mb-8 h-px bg-[#E8D5A3]" style={{ maxWidth: 120 }} aria-hidden="true" />
            {/* Day 1 preview — like the opening page of a journal */}
            <div className="mb-10 pl-6" style={{ borderLeft: "1px solid #E8D5A3" }} data-testid="story-week-preview">
              <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em] mb-3">DAY ONE</p>
              <p className="text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "clamp(1.5rem, 3vw, 2rem)", lineHeight: 1.4 }}>
                &ldquo;Is there a song or piece of music that takes you back to your childhood? What do you remember when you hear it?&rdquo;
              </p>
            </div>
            <p className="text-[#1B2A4A] max-w-2xl mb-3" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.65 }}>
              Story Week is your free doorway into The Living Book. Seven days, seven warm questions to ask someone you love. Sit together, press record, and hear the stories you&rsquo;ve never heard. Your first question is ready when you begin.
            </p>
            <p className="text-[#8C8278] max-w-2xl mb-8 italic" style={{ fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: "17px", lineHeight: 1.5 }}>
              Telling your own story? You can answer on your own too.
            </p>
            <button
              type="button"
              onClick={swCta.go}
              className="btn-gold"
              style={{ minHeight: 52, padding: "14px 28px", fontSize: "15px", letterSpacing: "0" }}
              data-testid="story-week-cta"
            >
              {swCta.label}
            </button>
            <AlreadyStartedLink tone="dark" testid="story-week-already-started" />
            <p className="mt-4 text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.6 }} data-testid="story-week-helper">
              The button opens a short free sign-up (name, email, password). You can answer Question One straight away. We'll send a confirmation link to your email so tomorrow's question and the rest of the week can arrive.
            </p>
          </div>
        </div>
      </section>
      {/* YOUR LIFE IN MUSIC — feature preview (form links to the Founding Beta) */}
      <section id="your-life-in-music" className="pt-12 pb-24 sm:pt-16 sm:pb-32 px-6 sm:px-10" style={{ background: "#111D33" }} data-testid="section-your-life-in-music">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          {/* Left — image with decorative gold record circle behind */}
          <div className="md:col-span-5 order-1">
            <div className="relative mx-auto" style={{ maxWidth: 440 }}>
              <div
                aria-hidden="true"
                className="absolute pointer-events-none"
                style={{
                  top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "115%", paddingBottom: "115%",
                  borderRadius: "50%",
                  border: "1px solid #B8973A",
                  opacity: 0.55,
                }}
              />
              <div
                aria-hidden="true"
                className="absolute pointer-events-none"
                style={{
                  top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "88%", paddingBottom: "88%",
                  borderRadius: "50%",
                  border: "1px solid #E8D5A3",
                  opacity: 0.55,
                }}
              />
              <figure className="relative" style={{ aspectRatio: "4 / 3" }}>
                <img
                  src="/img/music.webp"
                  alt="A wooden record player spinning a vinyl record, beside a stack of 45s with colourful labels"
                  width="1400"
                  height="989"
                  loading="lazy"
                  className="editorial-photo"
                  style={{ width: "100%", height: "100%" }}
                  data-testid="ylim-photo"
                />
              </figure>
            </div>
          </div>

          {/* Right — copy + CTA, left-aligned */}
          <div className="md:col-span-7 order-2 text-left">
            <p className="label-ui text-[11px] text-[#B8973A] mb-6 tracking-[0.3em]" data-testid="ylim-eyebrow">YOUR LIFE IN MUSIC</p>
            <div className="mb-8" style={{ width: 96 }} aria-hidden="true" data-testid="ylim-stave">
              <div className="h-px bg-[#B8973A] mb-[6px]" />
              <div className="h-px bg-[#E8D5A3] mb-[6px]" />
              <div className="h-px bg-[#B8973A]" />
            </div>
            <h2 className="text-4xl sm:text-5xl text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.1 }} data-testid="ylim-headline">
              When they hear that song, <span style={{ fontStyle: "italic", color: "#F2E3B8" }}>where does it take them?</span>
            </h2>
            <p className="mt-8 max-w-2xl text-[#E8D5A3]" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.65 }} data-testid="ylim-body">
              To someone they loved? A place they called home? The person they danced with? The year one album got them through? Put it on together, and ask. The music is easy to pass on. The reason it mattered only comes out in conversation. Some of the best conversations begin with a song.
            </p>
            <p className="mt-8 italic text-[#F2E3B8]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "clamp(1.25rem, 2.4vw, 1.55rem)", lineHeight: 1.45 }} data-testid="ylim-support">
              Where words fail, music starts the conversation.
            </p>
            <p className="mt-10 label-ui text-[10px] text-[#B8973A] tracking-[0.3em]" data-testid="ylim-status">
              PART OF THE FULL LIVING BOOK &middot; CURRENTLY IN FOUNDING BETA
            </p>
            {/* CTA removed on this panel — Story Week is started from the hero and the "together" panel below. */}
          </div>
        </div>
      </section>


      {/* HOW STORY WEEK WORKS — editorial three-step + final CTA */}
      <section id="how" className="relative bg-[#F7F0E0]" style={{ overflow: "hidden" }} data-testid="section-how-story-week">
        <style>{`
          .how-container {
            max-width: 1280px;
            margin: 0 auto;
            padding: 120px 64px;
          }
          .how-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 40px;
          }
          .how-header { order: 1; }
          .how-photo  { order: 2; }
          .how-steps  { order: 3; }

          .how-kicker {
            font-family: Montserrat, sans-serif;
            font-size: 11px;
            letter-spacing: 0.3em;
            text-transform: uppercase;
            color: #B8973A;
            margin: 0;
          }
          .how-headline {
            font-family: "Cormorant Garamond", serif;
            font-weight: 400;
            color: #111D33;
            line-height: 1.05;
            font-size: clamp(40px, 5vw, 60px);
            margin: 24px 0 0;
            max-width: none;
          }
          .how-headline em { font-style: italic; }
          .how-header { margin: 0; min-height: 0; }

          .how-steps { margin-top: 0; }
          .how-step {
            display: grid;
            grid-template-columns: 56px 1fr;
            column-gap: 24px;
            align-items: baseline;
            padding: 32px 0;
            border-bottom: 1px solid rgba(201,165,92,0.3);
          }
          .how-step:last-child { border-bottom: none; }
          .how-step .numeral {
            font-family: "Cormorant Garamond", serif;
            color: #B8973A;
            font-size: 28px;
            line-height: 1;
            margin: 0;
          }
          .how-step .step-title {
            font-family: Montserrat, sans-serif;
            font-size: 12px;
            letter-spacing: 0.22em;
            text-transform: uppercase;
            color: #111D33;
            margin: 0;
            line-height: 1;
          }
          .how-step .step-body {
            font-family: Georgia, serif;
            font-size: 20px;
            line-height: 1.5;
            color: #1B2A4A;
            margin: 10px 0 0;
            max-width: 34em;
          }

          .how-photo-wrap {
            position: relative;
            isolation: isolate;
            width: 100%;
            max-width: 360px;
            margin: 0 auto;
          }
          .how-photo-wrap::after {
            content: "";
            position: absolute;
            inset: 0;
            transform: translate(14px, 14px);
            border: 1px solid rgba(201, 165, 92, 0.5);
            z-index: -1;
            pointer-events: none;
          }
          .how-photo-img {
            width: 100%;
            aspect-ratio: 4 / 5;
            object-fit: cover;
            display: block;
            border: 1px solid #c9a55c;
            padding: 10px;
            background: transparent;
            box-sizing: border-box;
          }

          @media (min-width: 900px) {
            .how-grid {
              grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
              column-gap: 80px;
              row-gap: 56px;
              align-items: start;
            }
            .how-header { grid-column: 1; grid-row: 1; order: initial; }
            .how-steps  { grid-column: 1; grid-row: 2; order: initial; margin-top: 0; }
            .how-photo  { grid-column: 2; grid-row: 1 / span 2; order: initial; position: sticky; top: 120px; }
            .how-headline { font-size: clamp(40px, 4vw, 56px); }
            .how-photo-wrap { max-width: 420px; margin: 8px 0 0 auto; justify-self: end; }
          }
        `}</style>

        <div className="how-container">
          <div className="how-grid">
            <div className="how-header">
              <p className="how-kicker" data-testid="how-eyebrow">PART I &middot; HOW STORY WEEK WORKS</p>
              <h2 className="how-headline">
                What are the stories you&rsquo;ve <em>never quite found the moment to ask about?</em>
              </h2>
            </div>

            <div className="how-photo" data-testid="how-photo-wrap">
              <div className="how-photo-wrap">
                <img
                  className="how-photo-img"
                  src="/img/how-photos.webp"
                  alt="Family photographs laid out on a wooden table, one being lifted by a pair of hands"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="how-steps" data-testid="how-it-works-list">
              {[
                { n: "I",   title: "ONE QUESTION A DAY, FOR SEVEN DAYS", body: "Sit down with someone you love, ask it, and press record." },
                { n: "II",  title: "YOUR CONVERSATIONS BECOME THE FIRST PAGES", body: "We turn what they tell you into their story, in their own words and voice." },
                { n: "III", title: "THEN IT GROWS", body: "Add photographs, build their Life in Music, and invite the family to listen and add their own memories." },
              ].map((s) => (
                <div key={s.n} className="how-step" data-testid={`how-step-${s.n}`}>
                  <span className="numeral">{s.n}</span>
                  <div>
                    <h3 className="step-title">{s.title}</h3>
                    <p className="step-body">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final heading + CTA — kept under the grid, on the 8px scale */}
        <div className="text-center" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 120px" }} data-testid="how-final-cta">
          <h2 className="text-3xl sm:text-4xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.2, maxWidth: "36rem", marginLeft: "auto", marginRight: "auto" }}>
            There&rsquo;s so much more to them than you know. <span style={{ fontStyle: "italic" }}>Start with one question. Ask it together.</span>
          </h2>
          <button
            type="button"
            onClick={swCta.go}
            className="btn-gold"
            style={{ minHeight: 52, padding: "14px 32px", marginTop: 40, fontSize: "15px", letterSpacing: "0" }}
            data-testid="how-final-cta-btn"
          >
            {swCta.label}
          </button>
          <p className="text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.6, marginTop: 24 }} data-testid="how-final-helper">
            Free. No card. Private to your family. You choose who listens.
          </p>
          <AlreadyStartedLink tone="dark" testid="how-already-started" />
        </div>
      </section>

      {/* YOUR STORY MATTERS — solo-focus invitation */}
      <section id="together" className="paper-grain py-16 sm:py-14 px-6 sm:px-10 bg-[#FAF7F2]" data-testid="section-together">
        <div className="max-w-3xl mx-auto text-left">
          <p className="label-ui text-[11px] text-[#B8973A] mb-6 tracking-[0.3em]" data-testid="together-eyebrow">THEIR STORY MATTERS</p>
          <h2 className="text-4xl sm:text-5xl text-[#111D33] mb-8" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.1 }} data-testid="together-headline">
            Their story <span style={{ fontStyle: "italic" }}>matters.</span> So does the time you spend hearing it.
          </h2>
          <div className="mb-8 h-px bg-[#E8D5A3]" style={{ maxWidth: 96 }} aria-hidden="true" />
          <div className="space-y-6 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.65 }} data-testid="together-body">
            <p>
              One day, you&rsquo;ll want to know who they were at your age. How they made it through the hard years. What they&rsquo;d tell you now, if you&rsquo;d only thought to ask. And one day, your children will want to hear it in their voice.
            </p>
            <p>
              Every day, stories are lost because no one sat down and asked. An hour together on a Sunday is all it takes to keep them.
            </p>
            <p>
              Sit down together. Ask the first question.
            </p>
          </div>
          <button
            type="button"
            onClick={swCta.go}
            className="btn-gold mt-10"
            style={{ minHeight: 52, padding: "14px 32px", fontSize: "15px", letterSpacing: "0" }}
            data-testid="together-cta"
          >
            {swCta.label}
          </button>
          <AlreadyStartedLink tone="dark" testid="together-already-started" />
        </div>
      </section>

      {/* QUOTES STRIP + OUR DIFFERENCE + BOOK MOCKUP — hidden pre-launch per audit */}
      {SHOW_HOMEPAGE_EXTRAS && (
      <>
      {/* QUOTES STRIP */}
      <section className="py-14 px-6 sm:px-10" style={{ background: "#F7F0E0" }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
          {QUOTES.map((q, i) => (
            <div key={i} className="text-center" data-testid={`quote-${i}`}>
              <p className="italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.6 }}>"{q.q}"</p>
              <p className="label-ui text-[10px] text-[#B8973A] mt-4">{q.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OUR DIFFERENCE */}
      <section id="why" className="py-16 sm:py-14 px-6 sm:px-10 bg-[#FAF7F2]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="label-ui text-[11px] text-[#B8973A] mb-6">Our difference</p>
            <h2 className="text-4xl sm:text-5xl max-w-3xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Voice. Memory. <br/>
              <span style={{ fontStyle: "italic", color: "#B8973A" }}>A legacy.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F0EAE0]">
            {DIFFERENTIATORS.map((d, i) => (
              <div key={i} className="p-10 bg-[#FAF7F2]" data-testid={`differentiator-${i}`}>
                <div className="text-[#B8973A] mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1rem", letterSpacing: "0.02em" }}>✦</div>
                <h3 className="text-2xl mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>{d.title}</h3>
                <p className="text-[#8C8278] leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK MOCKUP */}
      <section className="py-16 sm:py-14 px-6 sm:px-10" style={{ background: "#111D33" }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="label-ui text-[11px] text-[#B8973A] mb-6">A book made of voices</p>
            <h2 className="text-4xl sm:text-5xl text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Hardback. Beautifully bound.<br/>
              <span style={{ fontStyle: "italic", color: "#E8D5A3" }}>A keepsake for your family.</span>
            </h2>
            <Ornament className="my-10 justify-start" color="#B8973A" />
            <p className="text-[#E8D5A3] leading-relaxed" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
              Every story, every photograph, every wise word — typeset into pages your family will hold in their hands. Personalised printing will be offered separately when fulfilment is ready.
            </p>
          </div>
          <div className="relative">
            <p className="label-ui text-[10px] text-[#8C8278] mb-3 tracking-[0.25em]" data-testid="illustrative-family-label">
              ILLUSTRATIVE FAMILY EXAMPLE
            </p>
            <div className="grid grid-cols-2 gap-3 hairline" style={{ padding: 16, background: "#1B2A4A", borderRadius: 4 }}>
              <div className="aspect-[3/4] flex flex-col items-center justify-center p-6 text-center" style={{ background: "#111D33", border: "1px solid #B8973A", borderRadius: 2 }}>
                <span className="text-[#B8973A] text-3xl mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
                <p className="label-ui text-[10px] text-[#B8973A] mb-4">Volume I</p>
                <h3 className="text-[#FAF7F2] text-2xl" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic" }}>The Marlowe Family</h3>
                <div className="my-6 w-12 h-px" style={{ background: "#B8973A" }} />
                <p className="text-[#E8D5A3] text-xs label-ui">A Living Book</p>
              </div>
              <div className="aspect-[3/4] p-6" style={{ background: "#FAF7F2", borderRadius: 2 }}>
                <p className="chapter-roman text-[10px] mb-3">III · FAMILY & HOME</p>
                <p className="prompt-italic text-xs mb-3">Tell me about your parents…</p>
                <p className="story-text text-[10px] leading-relaxed text-[#1B2A4A]">
                  My mother kept a small porcelain cat by the kitchen window. She said it watched over us when she could not. I think of her still, whenever the morning light catches a sill…
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </>
      )}

      {/* THE LIVING BOOK CLUB (hidden pre-launch) */}
      {SHOW_BOOK_CLUB && (
      <section id="book-club" className="py-16 sm:py-14 px-6 sm:px-10" style={{ background: "#111D33" }} data-testid="section-book-club">
        <div className="max-w-3xl mx-auto text-center">
          <p className="label-ui text-[11px] text-[#B8973A] mb-6 tracking-[0.25em]">THE LIVING BOOK CLUB</p>
          <h2 className="text-4xl sm:text-5xl text-[#FAF7F2]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400 }} data-testid="book-club-headline">
            A book club where <span style={{ fontStyle: "italic", color: "#E8D5A3" }}>the book is your life.</span>
          </h2>
          <Ornament className="my-8" />
          <p className="text-[#E8D5A3] italic max-w-2xl mx-auto mb-6" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.7 }}>
            Sunday mornings. Coffee and one question worth talking about. Come alone. Open the book.
            Leave knowing somebody. Gatherings in libraries, bookshop caf&eacute;s and independent
            spaces across the UK &mdash; opening autumn 2026.
          </p>
          <p className="text-[#8C8278] italic max-w-2xl mx-auto mb-10" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
            Founding membership includes <span style={{ fontStyle: "italic" }}>The Living Questions</span>,
            Story Week and invitations to gatherings near you.
          </p>

          {clubSubmitted ? (
            <p className="text-[#E8D5A3]" data-testid="book-club-success" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem", fontStyle: "italic" }}>
              Beautiful &mdash; you&apos;re on the founding list. We&apos;ll write when a gathering opens near you.
            </p>
          ) : (
            <form onSubmit={joinBookClub} className="max-w-xl mx-auto text-left" data-testid="book-club-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  value={club.name} onChange={(e) => setClub({ ...club, name: e.target.value })}
                  placeholder="Your name"
                  className="px-4 py-3 bg-[#1B2A4A] text-[#FAF7F2] placeholder-[#8C8278]"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", border: "1px solid #1B2A4A" }}
                  data-testid="book-club-name-input"
                />
                <input
                  value={club.email} onChange={(e) => setClub({ ...club, email: e.target.value })}
                  placeholder="Your email"
                  type="email" required
                  className="px-4 py-3 bg-[#1B2A4A] text-[#FAF7F2] placeholder-[#8C8278]"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", border: "1px solid #1B2A4A" }}
                  data-testid="book-club-email-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <input
                  value={club.city} onChange={(e) => setClub({ ...club, city: e.target.value })}
                  placeholder="Your UK town or city"
                  required
                  className="px-4 py-3 bg-[#1B2A4A] text-[#FAF7F2] placeholder-[#8C8278]"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", border: "1px solid #1B2A4A" }}
                  data-testid="book-club-city-input"
                />
                <select
                  value={club.role} onChange={(e) => setClub({ ...club, role: e.target.value })}
                  className="px-4 py-3 bg-[#1B2A4A] text-[#FAF7F2]"
                  style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", border: "1px solid #1B2A4A" }}
                  data-testid="book-club-role-select"
                >
                  <option value="attend">Attend a gathering</option>
                  <option value="host">Host a gathering</option>
                  <option value="either">Either &mdash; I&apos;m open</option>
                </select>
              </div>
              <div className="text-center">
                <button type="submit" disabled={clubBusy} className="btn-gold" data-testid="book-club-cta">
                  {clubBusy ? "One moment…" : "Join the Founding List — No payment today"}
                </button>
              </div>
              <label className="flex items-start gap-2 mt-5 max-w-lg mx-auto cursor-pointer" data-testid="book-club-consent-label">
                <input
                  type="checkbox"
                  checked={club.consent}
                  onChange={(e) => setClub({ ...club, consent: e.target.checked })}
                  className="mt-1 shrink-0"
                  data-testid="book-club-consent-checkbox"
                />
                <span className="text-xs text-[#8C8278]" style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}>
                  I agree to my details being stored to receive Book Club updates and invitations. See our{" "}
                  <Link to="/privacy" className="underline text-[#E8D5A3] hover:text-white">Privacy Policy</Link>. You can unsubscribe any time.
                </span>
              </label>
            </form>
          )}
          {clubError && <p className="text-red-300 mt-4 text-sm text-center" data-testid="book-club-error">{clubError}</p>}
        </div>
      </section>
      )}

      {/* THE LIVING QUESTIONS BOOK */}
      <section id="the-book" className="paper-grain py-16 sm:py-16 px-6 sm:px-10 bg-[#FAF7F2]" data-testid="section-the-book">
        <div className="max-w-4xl mx-auto text-center mb-14 sm:mb-20">
          <p
            className="text-[#1B2A4A]"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontStyle: "italic",
              fontSize: "clamp(1.5rem, 3vw, 2.1rem)",
              lineHeight: 1.4,
            }}
            data-testid="book-tagline"
          >
            We don&rsquo;t just preserve stories &mdash; we begin conversations.
          </p>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          {/* Left — 3D-tilted book cover */}
          <div className="md:col-span-5 order-1">
            <div className="relative mx-auto" style={{ maxWidth: 340, perspective: 1400 }}>
              <div
                aria-hidden="true"
                className="absolute pointer-events-none"
                style={{
                  bottom: "-4%", left: "10%", right: "10%", height: "6%",
                  background: "radial-gradient(ellipse at center, rgba(17,29,51,0.28), transparent 70%)",
                  filter: "blur(10px)",
                  borderRadius: "50%",
                }}
              />
              <figure
                style={{
                  aspectRatio: "3 / 4",
                  transform: "rotateY(-8deg) rotateX(2deg)",
                  transformStyle: "preserve-3d",
                  boxShadow:
                    "-14px 0 18px -14px rgba(17,29,51,0.28), 0 24px 40px -22px rgba(17,29,51,0.35)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <img
                  src="/living-questions-book.jpg"
                  alt="The Living Questions, a hardcover book by Andrea Faye"
                  loading="lazy"
                  width="600"
                  height="800"
                  className="w-full h-full object-cover block"
                  data-testid="the-book-cover-image"
                />
                {/* spine highlight */}
                <span aria-hidden="true" style={{
                  position: "absolute", top: 0, bottom: 0, left: 0, width: 10,
                  background: "linear-gradient(90deg, rgba(17,29,51,0.35), rgba(17,29,51,0) 70%)",
                  pointerEvents: "none",
                }} />
                {/* page edge highlight */}
                <span aria-hidden="true" style={{
                  position: "absolute", top: 0, bottom: 0, right: 0, width: 4,
                  background: "linear-gradient(-90deg, rgba(232,213,163,0.6), rgba(232,213,163,0) 90%)",
                  pointerEvents: "none",
                }} />
              </figure>
            </div>
          </div>

          {/* Right — copy + Amazon CTA */}
          <div className="md:col-span-7 order-2 text-left">
            <p className="label-ui text-[11px] text-[#B8973A] mb-6 tracking-[0.25em]">THE LIVING QUESTIONS &middot; ANDREA FAYE</p>
            <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, color: "#111D33", lineHeight: 1.15 }} data-testid="the-book-headline">
              <span style={{ fontStyle: "italic" }}>The Living Questions</span> is a beautifully guided book created to begin the conversations that matter.
            </h2>
            <div className="mt-8 mb-8 h-px bg-[#E8D5A3]" style={{ maxWidth: 120 }} aria-hidden="true" />
            <p className="max-w-2xl mb-4 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.75 }}>
              90 guided questions and space for 10 conversations of your own. <span style={{ fontStyle: "italic" }}>A journey through a life.</span>
            </p>
            <p className="max-w-2xl mb-10 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.75 }}>
              Use it with a parent, grandparent, partner or friend&mdash;or answer the questions yourself.
            </p>
            {AMAZON_URL ? (
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-block"
                style={{ minHeight: 44 }}
                data-testid="the-book-cta"
              >
                Buy The Living Questions on Amazon
              </a>
            ) : (
              <span
                className="inline-block btn-outline-navy cursor-default"
                style={{ minHeight: 44, opacity: 0.85 }}
                aria-disabled="true"
                data-testid="the-book-cta"
              >
                Available on Amazon soon
              </span>
            )}
          </div>
        </div>
      </section>

      {/* FOUNDING BETA — replaces paid membership section pre-launch */}
      <section id="founding-beta" className="paper-grain py-16 sm:py-16 px-6 sm:px-10 bg-[#FAF7F2]" data-testid="section-founding-beta">
        <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <span className="chapter-roman" style={{ fontSize: "0.85rem" }}>PART II</span>
            <p className="label-ui text-[11px] text-[#B8973A] mt-4 tracking-[0.3em]">THE FULL LIVING BOOK</p>
            <p className="mt-6 text-[#5C5548] italic hidden md:block" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", lineHeight: 1.55, maxWidth: "16rem" }}>
              Story Week is live. The full <span style={{ color: "#B8973A" }}>Living Book</span> is coming soon.
            </p>

            {/* Overlapping archival snapshot collage */}
            <div
              className="relative mt-10 mx-auto md:mx-0"
              style={{ maxWidth: 260, height: 240 }}
              role="img"
              aria-label="A small collage of archival family snapshots and a handwritten letter"
              data-testid="beta-collage"
            >
              {[
                { src: "/img/collage-1.webp", rot: 0,  top: 60, left: 40,  w: 170, alt: "A grandmother and a young girl smiling as they bake cookies together in a kitchen", z: 3 },
                { src: "/img/collage-2.webp", rot: 2,  top: 20, left: 105, w: 150, alt: "A bundle of pale-blue letters tied with twine, a vintage photograph and a handwritten note on aged wood", z: 2 },
                { src: "/img/collage-3.webp", rot: -3, top: 8,  left: -5,  w: 150, alt: "An elderly man gently holding two vintage black-and-white photographs, remembering", z: 1 },
              ].map((p, i) => (
                <img
                  key={i}
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="editorial-photo absolute"
                  style={{
                    top: p.top, left: p.left, width: p.w, height: p.w,
                    transform: `rotate(${p.rot}deg)`,
                    zIndex: p.z,
                  }}
                />
              ))}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="text-4xl sm:text-5xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.1 }} data-testid="founding-beta-headline">
              The Full <span style={{ color: "#B8973A" }}>Living Book</span> Is <span style={{ fontStyle: "italic" }}>Coming Soon</span>
            </h2>
            <div className="mt-8 mb-8 h-px bg-[#E8D5A3]" style={{ maxWidth: 120 }} aria-hidden="true" />
            <div className="flex flex-wrap gap-2 mb-6" data-testid="availability-legend">
              <span className="label-ui" style={{ fontSize: "10px", letterSpacing: "0.02em", padding: "6px 10px", background: "#EAF3EE", border: "1px solid #B7D6C4", borderRadius: 3, color: "#1B2A4A" }}>STORY WEEK · LIVE NOW</span>
              <span className="label-ui" style={{ fontSize: "10px", letterSpacing: "0.02em", padding: "6px 10px", background: "#FFF7E5", border: "1px solid #E8D5A3", borderRadius: 3, color: "#1B2A4A" }}>FULL LIVING BOOK · CLOSED BETA</span>
              <span className="label-ui" style={{ fontSize: "10px", letterSpacing: "0.02em", padding: "6px 10px", background: "#F2EEE6", border: "1px solid #D9CFBE", borderRadius: 3, color: "#5C5548" }}>THEIR LIFE IN MUSIC (FULL VERSION) · COMING</span>
            </div>
            <p className="text-[#1B2A4A] max-w-2xl mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.65 }} data-testid="founding-beta-body">
              Start with <strong>Story Week</strong> above. It&rsquo;s free and open now. Once you&rsquo;ve shared seven conversations with someone you love, we&rsquo;ll send you a one-tap invitation to help beta test the full Living Book. There&rsquo;s no new account and no form to fill in.
            </p>
            <p className="text-[#1B2A4A] max-w-2xl mb-6" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.65 }} data-testid="founding-beta-pricing">
              Rather register your interest first? You can do that below. The first <strong>50 members</strong> keep the <strong>Founding Member</strong> rate: <strong>&pound;49 a year</strong> for one Storyteller, or <strong>&pound;89 a year</strong> for the whole family (up to four Storytellers). <strong>450 places</strong> in the beta founding round at <strong>&pound;79</strong> for one Storyteller and <strong>&pound;129</strong> for the family (up to four Storytellers).
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <button
                type="button"
                onClick={swCta.go}
                className="btn-gold"
                style={{ minHeight: 52, padding: "14px 28px", fontSize: "15px", letterSpacing: "0" }}
                data-testid="founding-beta-storyweek-cta"
              >
                Start Story Week first
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!user) {
                    navigate(`/login?flow=founding_beta&next=${encodeURIComponent("/founding-beta")}`);
                  } else {
                    navigate("/founding-beta");
                  }
                }}
                className="inline-flex items-center justify-center transition-colors"
                style={{
                  minHeight: 52, padding: "14px 28px",
                  fontSize: "15px", letterSpacing: "0",
                  fontFamily: "Montserrat, sans-serif",
                  background: "transparent",
                  color: "#B8973A",
                  border: "1px solid #B8973A",
                  borderRadius: 4,
                }}
                data-testid="founding-beta-cta"
              >
                Register beta testing interest
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* EDITIONS (personalised printed editions — hidden until fulfilment is ready) */}
      {SHOW_EDITIONS && (
      <section id="editions" className="py-16 sm:py-14 px-6 sm:px-10 bg-[#F7F0E0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="label-ui text-[11px] text-[#B8973A] mb-6">Editions</p>
            <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              When you're ready, <span style={{ fontStyle: "italic" }}>hold it in your hands.</span>
            </h2>
            <p className="mt-6 italic text-[#8C8278] max-w-2xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
              Story Keepers save 15–25% on every printed book.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {EDITIONS.map((e, i) => (
              <div key={e.id} className="p-10 bg-white hairline-cream relative" style={{ borderRadius: 4 }} data-testid={`edition-card-${e.id}`}>
                {e.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 label-ui text-[10px]" style={{ background: "#B8973A", color: "#FAF7F2", borderRadius: 2 }}>{e.badge}</div>
                )}
                <p className="chapter-roman mb-4">{["I","II","III"][i]}</p>
                <h3 className="text-2xl mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{e.name}</h3>
                <p className="text-[#8C8278] text-sm mb-6">{e.size}</p>
                <div className="mb-2"><span className="text-4xl" style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33" }}>£{e.price}</span></div>
                <p className="label-ui text-[10px] text-[#B8973A] mb-5">Founding members save 20–25%</p>
                <p className="text-[#8C8278] text-sm leading-relaxed">{e.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* B2B (partnerships — hidden until commercially ready) */}
      {SHOW_B2B && (
      <section id="b2b" className="py-16 sm:py-14 px-6 sm:px-10 bg-[#FAF7F2]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-ui text-[11px] text-[#B8973A] mb-6">For organisations</p>
            <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              For organisations that <span style={{ fontStyle: "italic" }}>serve families.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F0EAE0] mb-12">
            {B2B_TIERS.map((t, i) => (
              <div key={t.id} className="p-8 bg-[#FAF7F2]" data-testid={`b2b-tier-${t.id}`}>
                <p className="chapter-roman mb-3">{["I","II","III","IV"][i]}</p>
                <h3 className="text-xl mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t.name}</h3>
                <p className="text-[#8C8278] text-sm leading-relaxed mb-4">{t.body}</p>
                <p className="label-ui text-[10px] text-[#B8973A]">{t.price_blurb}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => setB2bOpen(true)} className="btn-primary" data-testid="b2b-enquire-btn">Enquire about partnerships</button>
          </div>
        </div>
      </section>
      )}

      {/* Founder section removed pre-launch — book image, Amazon link and legal operator details remain elsewhere. */}

      {/* QUOTE BREAK — original Living Book line (Nietzsche/Bach etc removed) */}
      <section className="relative overflow-hidden py-16 px-6 sm:px-10 text-center" style={{ background: "#F7F0E0" }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/img/letters-bg.webp')",
            backgroundSize: "cover",
            backgroundPosition: "70% 80%",
            opacity: 0.10,
          }}
        />
        <div className="relative">
          <Ornament />
          <p className="mt-10 mx-auto max-w-3xl text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "clamp(1.4rem, 3vw, 2rem)", lineHeight: 1.55 }}>
            &ldquo;The conversation is the gift. The book is simply where it comes to rest.&rdquo;
          </p>
          <p className="label-ui text-[10px] text-[#B8973A] mt-6">The Living Book</p>
        </div>
      </section>

      {/* FINAL Start Story Week INVITATION */}
      <section id="begin" className="relative overflow-hidden py-16 sm:py-16 px-6 sm:px-10" style={{ background: "#111D33" }} data-testid="section-final-cta">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/img/shoebox-bg.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.22,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 55% at 50% 50%, rgba(17,29,51,0.94), rgba(17,29,51,0.75) 60%, rgba(17,29,51,0.55) 100%)",
          }}
        />
        <div className="relative max-w-2xl mx-auto text-center">
          <p className="label-ui text-[11px] text-[#B8973A] mb-6 tracking-[0.3em]">BEGIN</p>
          <h2 className="text-[#FAF7F2] text-4xl sm:text-5xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.15 }}>
            Seven days. Seven questions. <span style={{ fontStyle: "italic", color: "#E8D5A3" }}>Yours to keep.</span>
          </h2>
          <p className="text-[#E8D5A3] mb-10" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", fontStyle: "italic" }}>
            Story Week is free. No card. One question a day. Begin whenever you're ready.
          </p>
          <button
            onClick={swCta.go}
            className="btn-gold px-8 py-4"
            style={{ minHeight: 52, fontSize: "15px", letterSpacing: "0" }}
            data-testid="final-cta-btn"
          >
            {swCta.label}
          </button>
        </div>
      </section>

      {/* WAITLIST (temporarily hidden pre-launch) */}
      {SHOW_WAITLIST && (
      <section id="waitlist" className="py-16 sm:py-14 px-6 sm:px-10" style={{ background: "#111D33" }}>
        <div className="max-w-2xl mx-auto text-center">
          <p className="label-ui text-[11px] text-[#B8973A] mb-6">The waitlist</p>
          <h2 className="text-[#FAF7F2] text-4xl sm:text-5xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Be among the first families.
          </h2>
          <p className="text-[#E8D5A3] mb-10" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", fontStyle: "italic" }}>
            Join the waitlist for early access and founding member pricing.
          </p>
          {submitted ? (
            <p className="text-[#E8D5A3]" data-testid="waitlist-success" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem", fontStyle: "italic" }}>
              Beautifully received. We'll be in touch.
            </p>
          ) : (
            <form onSubmit={joinWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="waitlist-form">
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-5 py-4 bg-transparent text-[#FAF7F2] placeholder:text-[#8C8278]"
                style={{ border: "1px solid #B8973A", borderRadius: 4, fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}
                data-testid="waitlist-email-input"
              />
              <button type="submit" className="btn-gold" data-testid="waitlist-submit-btn">Begin Story Week &mdash; Free</button>
            </form>
          )}
          {error && <p className="text-red-300 mt-4 text-sm" data-testid="waitlist-error">{error}</p>}
        </div>
      </section>
      )}

      {/* FOOTER */}
      <footer className="py-16 px-6 sm:px-10 bg-[#111D33] border-t border-[#1B2A4A]">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
            <span className="text-[#E8D5A3]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", letterSpacing: "0.06em" }}>The Living Book</span>
          </div>
          <p className="text-[#8C8278] text-sm italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            &ldquo;Memories surface when they&rsquo;re ready.&rdquo;
          </p>
          <Link to="/login" className="label-ui text-[11px] text-[#B8973A] mt-8 inline-block hover:text-[#E8D5A3] transition-colors" data-testid="footer-login-link">
            Already begun? Sign in →
          </Link>
        </div>
      </footer>
      <LegalFooter tone="dark" />

      {/* B2B MODAL */}
      {b2bOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10 overflow-y-auto" style={{ background: "rgba(17,29,51,0.8)" }} onClick={() => setB2bOpen(false)} data-testid="b2b-modal">
          <div className="max-w-xl w-full bg-[#FAF7F2] p-8 sm:p-10" style={{ borderRadius: 4 }} onClick={(e) => e.stopPropagation()}>
            <p className="label-ui text-[11px] text-[#B8973A] text-center mb-3">Partnership enquiry</p>
            <h3 className="text-3xl text-center mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>Tell us about your organisation</h3>
            {b2bSent ? (
              <div className="text-center py-6" data-testid="b2b-success">
                <Ornament />
                <p className="mt-6 italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
                  Beautifully received. We'll write to you within two working days.
                </p>
                <button onClick={() => { setB2bOpen(false); setB2bSent(false); }} className="btn-outline-navy mt-6" data-testid="b2b-close-btn">Close</button>
              </div>
            ) : (
              <form onSubmit={submitB2B} className="space-y-4" data-testid="b2b-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required value={b2b.org_name} onChange={(e) => setB2b({ ...b2b, org_name: e.target.value })}
                    placeholder="Organisation name" className="px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="b2b-org-name" />
                  <select value={b2b.org_type} onChange={(e) => setB2b({ ...b2b, org_type: e.target.value })}
                    className="px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="b2b-org-type">
                    <option value="care_home">Care Home</option>
                    <option value="funeral_director">Funeral Director</option>
                    <option value="corporate">Corporate</option>
                    <option value="genealogy">Genealogy Society</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={b2b.contact_name} onChange={(e) => setB2b({ ...b2b, contact_name: e.target.value })}
                    placeholder="Your name" className="px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="b2b-contact-name" />
                  <input required type="email" value={b2b.contact_email} onChange={(e) => setB2b({ ...b2b, contact_email: e.target.value })}
                    placeholder="Your email" className="px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="b2b-contact-email" />
                </div>
                <input value={b2b.size} onChange={(e) => setB2b({ ...b2b, size: e.target.value })}
                  placeholder="Size (e.g. 40 residents · 12 funeral arrangers)" className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="b2b-size" />
                <textarea value={b2b.message} onChange={(e) => setB2b({ ...b2b, message: e.target.value })} rows={3}
                  placeholder="A few words about what you'd like to explore (optional)"
                  className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.6 }} data-testid="b2b-message" />
                {b2bError && <p className="text-red-700 text-sm" data-testid="b2b-error">{b2bError}</p>}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setB2bOpen(false)} className="btn-outline-navy flex-1" data-testid="b2b-cancel-btn">Cancel</button>
                  <button type="submit" className="btn-primary flex-1" data-testid="b2b-submit-btn">Send enquiry</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
